import React, { useEffect, useRef, useState } from "react";
import "./CookieConsent.css";

const COOKIE_CONSENT_KEY = "nmdfc_cookie_consent";

// Checks the visitor's saved acceptance safely.
const getSavedConsent = () => {
  try {
    return window.localStorage.getItem(COOKIE_CONSENT_KEY) === "accepted";
  } catch {
    return false;
  }
};

// Displays cookie choices for first-time website visitors.
const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(!getSavedConsent());
  const rejectButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return undefined;

    previousFocusRef.current = document.activeElement;
    const frameId = window.requestAnimationFrame(() => {
      rejectButtonRef.current?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isVisible]);

  const dismissConsent = () => {
    const previousFocus = previousFocusRef.current;
    const target = previousFocus?.isConnected && previousFocus !== document.body
      ? previousFocus
      : document.getElementById("main-content");
    target?.focus({ preventScroll: true });
    setIsVisible(false);
  };

  // Saves acceptance and closes the popup.
  const saveConsent = () => {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    } catch {
      // Keeps the interface usable when storage is unavailable.
    }

    dismissConsent();
  };

  // Closes rejection without saving visitor consent.
  const handleReject = () => {
    try {
      window.localStorage.removeItem(COOKIE_CONSENT_KEY);
    } catch {
      // Keeps rejection usable when storage is unavailable.
    }

    dismissConsent();
  };

  // Saves the visitor's acceptance choice.
  const handleAccept = () => {
    saveConsent();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <section
      className="cookie-consent"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
    >
      <h2 className="cookie-consent__title" id="cookie-consent-title">
        We value your privacy
      </h2>
      <p
        className="cookie-consent__description"
        id="cookie-consent-description"
      >
        We use cookies to enhance your browsing experience, serve personalised
        ads or content, and analyse our traffic.
      </p>
      <div className="cookie-consent__actions">
        <button
          ref={rejectButtonRef}
          className="cookie-consent__button"
          type="button"
          onClick={handleReject}
        >
          Reject All
        </button>
        <button
          className="cookie-consent__button"
          type="button"
          onClick={handleAccept}
        >
          Accept All
        </button>
      </div>
    </section>
  );
};

export default CookieConsent;
